import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import GriStandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import GriFormComponent from "components/customizedStandardsComponent/GriFormComponent";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import { DETAILED_ACTIVITY_UN_RISK_ASSESS } from "data/standards/griStandards";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import {
  ACTIVITY_DETAIL_UN_RISK_ASSESS,
  UNIVERSAL_RISK_ASSESSMENT_GENERATOR_FORM,
} from "data/forms/griFormSchema";
import {
  identifyIndustryWorkEnviroment,
  typeOfRiskAssessment,
  verifyRiskAssessment,
  verifyRiskAssessmentReports,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";

const UniversalRiskAssessGenStepper = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const steps = [
    "Identify Industry and Work Environment",
    "Select the Type of Risk Assessment",
    "Provide Detailed Activity and Site Information",
    "Generate Universal Risk Assessment",
    "Upload Evidence",
    "Verify Risk Assessment Types",
    "Review and Confirm Risk Assessment",
    "Upload Evidence and Review Reports",
    "Download Post Review Gap Form",
    "Upload Post Review Gap Form",
    "Download Final Post Review Reports",
  ];

  useEffect(() => {
      if (activeStep === 7 && prevStep === 6) {
          setShowReviewPopup(true);
      }
  }, [activeStep, prevStep]);

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(8);
    setShowReviewPopup(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            key="identify-industry"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={identifyIndustryWorkEnviroment}
            title="Identify Industry and Work Environment"
            ReportDownloadButtonShow={false}
          />
        );
      case 1:
        return (
          <DownloadReport
            key="risk-assessment"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={typeOfRiskAssessment}
            title="Select the Type of Risk Assessment"
            ReportDownloadButtonShow={false}
          />
        );
      // case 2:
      //   return (
      //     <ReusableFormComponent
      //       {...ACTIVITY_DETAIL_UN_RISK_ASSESS}
      //       onNext={handleNext}
      //       onPrevious={handleBack}
      //       showPreviousButton={true}
      //     />
      //   );
      case 2:
        return (
          <StandardsSelector
            standards={DETAILED_ACTIVITY_UN_RISK_ASSESS}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Provide Detailed Activity and Site Information"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
            initialActivityDetails={reportData.activityDetails || {}}
            onActivityDetailsChange={(details) => {
              setReportData((prev) => ({
                ...prev,
                activityDetails: details,
              }));
            }}
          />
        );
      case 3:
        return (
          <ReusableFormComponent
            {...UNIVERSAL_RISK_ASSESSMENT_GENERATOR_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 4:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 5:
        return (
          <DownloadReport
            key="risk-assessment-types"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={verifyRiskAssessment}
            showReportCountdownButton={false}
            ReportDownloadButtonShow={false}
            title="Upload any documents that support, verify, or inform the risk assessment. Such as"
          />
        );
      case 6:
        return (
          <DownloadReport
            key="risk-assessment-types"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={verifyRiskAssessmentReports}
            showReportCountdownButton={true}
          />
        );
      case 7:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 8:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 9:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 10:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            showReportCountdownButton={true}
            standardName="Universal Risk Assessment"
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg3_4"
                title={"Universal Risk Assessment Generator"}
                activeStep={activeStep}
                onBack={handleBack}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          ></Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default UniversalRiskAssessGenStepper;
